/**
 * Backend sometimes prepends the API host to an already-absolute S3 URL, e.g.
 * https://carsapi.saldr.tech/https://cars-saldr-tech.s3.amazonaws.com/...
 * Prefer the Amazon / absolute nested URL — never keep the API prefix.
 */
export function sanitizeImageUrl(url) {
  if (!url || typeof url !== "string") {
    return "";
  }

  let trimmed = url.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("/http://") || trimmed.startsWith("/https://")) {
    trimmed = trimmed.slice(1);
  }

  // Prefer embedded Amazon S3 URL whenever the string is prefixed/corrupted.
  // Host must contain amazonaws.com (do not match from an earlier https://).
  const amazonMatch = trimmed.match(
    /https?:\/\/[^/\s"'<>\\]*amazonaws\.com[^\s"'<>\\]*/i
  );
  if (amazonMatch) {
    const startsWithAmazon = /^https?:\/\/[^/]*amazonaws\.com/i.test(trimmed);
    if (!startsWithAmazon) {
      return amazonMatch[0];
    }
  }

  // Nested absolute anywhere: https://api.host/.../https://bucket...
  const nestedAnywhere = trimmed.match(
    /https?:\/\/[^/\s]+\/(?:[^\s"'<>\\]*?)(https?:\/{1,2}[^\s"'<>\\]+)/i
  );
  if (nestedAnywhere) {
    let inner = nestedAnywhere[1];
    inner = inner.replace(/^http:\/(?!\/)/i, "http://");
    inner = inner.replace(/^https:\/(?!\/)/i, "https://");
    return inner;
  }

  // Nested absolute right after host: https://api.host/https://bucket...
  const nestedAbsolute = trimmed.match(
    /^https?:\/\/[^/]+\/(https?:\/{1,2}.+)$/i
  );
  if (nestedAbsolute) {
    let inner = nestedAbsolute[1];
    inner = inner.replace(/^http:\/(?!\/)/i, "http://");
    inner = inner.replace(/^https:\/(?!\/)/i, "https://");
    return inner;
  }

  return trimmed;
}

/**
 * Pick the best vehicle/product image from common API shapes.
 */
export function resolveVehicleImageUrl(item, fallback = "") {
  if (!item || typeof item !== "object") {
    return fallback;
  }

  const candidates = [
    Array.isArray(item.product_images) && item.product_images[0]
      ? item.product_images[0].image || item.product_images[0].url
      : null,
    item.main_image,
    item.product_image,
    Array.isArray(item.images) ? item.images[0] : null,
    item.image_method,
  ];

  for (const candidate of candidates) {
    const clean = sanitizeImageUrl(candidate);
    if (clean && !/notavailable\.jpg/i.test(clean)) {
      return clean;
    }
  }

  for (const candidate of candidates) {
    const clean = sanitizeImageUrl(candidate);
    if (clean) {
      return clean;
    }
  }

  return fallback;
}

const IMAGE_KEY_HINT =
  /image|photo|thumb|logo|avatar|picture|media|banner|icon/i;

/**
 * Walk API payloads and fix known image fields / nested absolute URLs.
 */
export function deepSanitizeImageUrls(value, depth = 0) {
  if (value == null || depth > 14) {
    return value;
  }

  if (typeof value === "string") {
    if (
      /amazonaws\.com/i.test(value) &&
      /^https?:\/\//i.test(value) &&
      !/^https?:\/\/[^/]*amazonaws\.com/i.test(value)
    ) {
      return sanitizeImageUrl(value);
    }
    if (/^https?:\/\/[^/]+\/(?:.*?)https?:/i.test(value)) {
      return sanitizeImageUrl(value);
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => deepSanitizeImageUrls(item, depth + 1));
  }

  if (typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((key) => {
      const child = value[key];
      if (typeof child === "string" && IMAGE_KEY_HINT.test(key)) {
        out[key] = sanitizeImageUrl(child);
      } else {
        out[key] = deepSanitizeImageUrls(child, depth + 1);
      }
    });
    return out;
  }

  return value;
}
